import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scrolls to the element matching `location.hash` when the hash changes.
 * Uses a MutationObserver to wait for elements that render after async data loads.
 */
export default function useScrollToHash() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return undefined;

    const id = hash.slice(1);

    const scrollTo = (el: Element) =>
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Element may already be in the DOM
    const existing = document.getElementById(id);
    if (existing) {
      scrollTo(existing);
      return undefined;
    }

    // Otherwise observe for it to appear
    const observer = new MutationObserver(() => {
      const el = document.getElementById(id);
      if (el) {
        observer.disconnect();
        scrollTo(el);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [hash]);
}
